import { FormNotice, Tenants, theme } from '@zappyrent/hundop';
import { AxiosError, AxiosResponse } from 'axios';
import { isEmpty, upperFirst } from 'lodash';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';

import { useToastContext } from '../../../contexts/ToastProvider';
import { StyledH2 } from '../../../pages/UploadDocuments/UploadDocuments.styles';
import tenantLandlordReferralApi from '../../../services/api/tenant-landlord-referral';
import { ILandlortReferral } from '../../../shared/ts/landlord-referrals';
import TelephoneInput from '../../___design/TelephoneInput';
import TextInput from '../../___design/TextInput';
import {
  ButtonContainer,
  RowContainer,
  StyledAddPaymentCta,
  StyledAddPaymentCtaIcon,
  StyledAddPaymentCtaLabel,
  StyledButton,
  StyledButtonWrapper,
  StyledContinueButton,
  StyledDeleteIcon,
  StyledSpinner,
  StyledSubSection,
  StyledSubText,
} from './AddALandlordReferral.styles';
import { tenantLandlordReferralFields } from './constant/tenant-landlord-referral-field.contant';

const LandlordReferralForm = ({
  index,
  landlordReferral,
  userId,
  bookingId,
  webpage_source,
  handleOnDelete,
  refetchLandlordReferrals,
  referralInfo,
}: {
  index: number;
  landlordReferral: ILandlortReferral;
  userId: number;
  bookingId: string;
  webpage_source: string;
  referralInfo: string;
  handleOnDelete: (index: number) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refetchLandlordReferrals: any;
}): JSX.Element => {
  const toast = useToastContext();
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const { t } = useTranslation(['form', 'documentsflow', 'routes']);
  const { mutateAsync: mutateLandlordReferralsCreateAsync } = useMutation(
    tenantLandlordReferralApi.createLandlordReferralsForBooking,
  );
  const { control, formState, watch, handleSubmit } = useForm({
    defaultValues: {
      lead_name: '',
      lead_phone_number: '',
      time_to_contact: '',
    },
  });
  const values = watch();
  const areInputsFilled = useMemo(() => {
    return Object.entries(values).every(([key, value]) => {
      if (key === 'time_to_contact') {
        return true;
      }
      return value !== '' && value !== null && value !== undefined;
    });
  }, [values]);

  const handleFormSubmit = async (
    values: Pick<
      ILandlortReferral,
      'lead_name' | 'lead_phone_number' | 'time_to_contact'
    >,
  ) => {
    if (!isEmpty(formState.errors)) {
      toast.error(upperFirst(t('errors:error')));
      return;
    }

    try {
      setSubmitLoading(true);
      await mutateLandlordReferralsCreateAsync({
        userId,
        bookingId,
        webpageSource: webpage_source,
        data: values,
      });

      toast.success(
        upperFirst(t('documentsflow:addANewReferralSubmittedSuccessFully')),
      );
      refetchLandlordReferrals();
      setSubmitLoading(false);
    } catch (error) {
      toast.error(
        upperFirst(t('documentsflow:addANewReferralSubmittedSuccessFully')),
      );
      setSubmitLoading(false);
    }
  };

  return (
    <>
      <RowContainer>
        <StyledH2>{upperFirst(t('documentsflow:addANewReferral'))}</StyledH2>
        {!landlordReferral.id && (
          <StyledButtonWrapper>
            <StyledButton
              onClick={() => handleOnDelete(index)}
              title={t('a11y:deleteTenant')}
            >
              <StyledDeleteIcon />
            </StyledButton>
          </StyledButtonWrapper>
        )}
      </RowContainer>
      <StyledSubText>{referralInfo}</StyledSubText>
      <form
        id="listing-detail-form"
        onSubmit={handleSubmit(handleFormSubmit)}
        noValidate
        style={{ width: '100%' }}
      >
        {tenantLandlordReferralFields.map(
          ({ name, required, fieldType }, index) => {
            if (fieldType === 'text') {
              return (
                <Controller
                  key={`tenant-landlord-referral-field-${name}-${index}`}
                  control={control}
                  name={name}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <TextInput
                      id={name}
                      label={upperFirst(t(`form:${name}`))}
                      name={name}
                      type="text"
                      value={landlordReferral[name] || value}
                      onChange={onChange}
                      required={required}
                    >
                      {error?.message && (
                        <FormNotice isWrapped={true}>
                          {t('form:requiredField')}
                        </FormNotice>
                      )}
                    </TextInput>
                  )}
                />
              );
            }
            if (fieldType === 'number') {
              return (
                <Controller
                  key={`tenant-landlord-referral-field-${name}-${index}`}
                  control={control}
                  name={name}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <TelephoneInput
                      id={name}
                      label={upperFirst(t(`form:${name}`))}
                      name={name}
                      value={landlordReferral[name] || value}
                      onChange={value => onChange(value.split(' ').join(''))}
                    >
                      {error?.message && (
                        <FormNotice isWrapped={true}>
                          {t('form:requiredField')}
                        </FormNotice>
                      )}
                    </TelephoneInput>
                  )}
                />
              );
            }
          },
        )}
        {!landlordReferral.id && (
          <ButtonContainer>
            <StyledContinueButton
              disabled={!areInputsFilled}
              title={t('common:confirm')}
            >
              {submitLoading && (
                <StyledSpinner
                  foregroundColor={theme.colors.cross.ultralightGray}
                  size={15}
                  strokeSize={2}
                />
              )}
              {t('common:confirm')}
            </StyledContinueButton>
          </ButtonContainer>
        )}
      </form>
      <br />
    </>
  );
};

const AddALandlordReferral = ({
  booking_id,
  user_id,
  referralInfo,
  webpage_source,
}: {
  user_id: number;
  referralInfo: string;
  booking_id: string;
  webpage_source: string;
}): JSX.Element | null => {
  const { t } = useTranslation(['form', 'documentsflow', 'routes']);
  const [landlordReferrals, setLandlordReferrals] = useState<
    ILandlortReferral[]
  >([]);

  const { refetch: refetchLandlordReferrals } = useQuery({
    queryKey: ['landlord-referral', user_id, booking_id],
    queryFn: () =>
      tenantLandlordReferralApi.getLandlordReferrals({
        userId: user_id,
        bookingId: booking_id,
        webpage_source,
      }),
    onSuccess: (response: AxiosResponse<Array<ILandlortReferral>>) => {
      setLandlordReferrals(response.data);
    },
    onError(error: AxiosError) {
      console.error(error);
    },
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    enabled: !!user_id,
  });

  const handleAddLandlordReferral = () => {
    setLandlordReferrals(prev => [
      ...prev,
      {
        lead_name: '',
        lead_phone_number: '',
        time_to_contact: '',
      },
    ]);
  };

  const handleLandlordReferralDelete = (index: number) => {
    setLandlordReferrals(prevReferral => {
      const updatedReferral = [...prevReferral];
      updatedReferral.splice(index, 1);
      return updatedReferral;
    });
  };

  return (
    <>
      {landlordReferrals.map((landlordReferral, index) => (
        <LandlordReferralForm
          key={`landlord-referral-form-${index}`}
          index={index}
          userId={user_id}
          bookingId={booking_id}
          landlordReferral={landlordReferral}
          handleOnDelete={handleLandlordReferralDelete}
          refetchLandlordReferrals={refetchLandlordReferrals}
          webpage_source={webpage_source}
          referralInfo={referralInfo}
        />
      ))}
      <StyledSubSection>
        <StyledH2>{upperFirst(t('documentsflow:addANewReferral'))}</StyledH2>
        <StyledAddPaymentCta
          onClick={handleAddLandlordReferral}
          title={t('documentsflow:addANewReferral')}
        >
          <StyledAddPaymentCtaIcon>
            <Tenants />
          </StyledAddPaymentCtaIcon>
          <StyledAddPaymentCtaLabel>
            {t('documentsflow:addANewReferral')}
          </StyledAddPaymentCtaLabel>
        </StyledAddPaymentCta>
      </StyledSubSection>
    </>
  );
};

export default AddALandlordReferral;
